import type { VcbSensitivityLevelDto } from './vcb-security';

export type OrgIntelligenceJsonValueDto =
  | null
  | boolean
  | number
  | string
  | OrgIntelligenceJsonValueDto[]
  | { [key: string]: OrgIntelligenceJsonValueDto };

export type OrgIntelligenceJsonObjectDto = Record<string, OrgIntelligenceJsonValueDto>;

export const ORG_INTELLIGENCE_NODE_KINDS = [
  'strategic_objective',
  'value_driver',
  'operating_lever',
  'execution_wave',
  'role',
  'team',
  'capability',
  'owner',
  'finance_baseline',
  'economic_claim',
  'execution_signal',
  'enterprise_constraint',
  'evidence_source',
  'assumption',
  'gap',
  'refusal',
  'supervision_record',
  'decision',
  'control_metric',
  'outcome_signal',
] as const;

export type OrgIntelligenceNodeKindDto = (typeof ORG_INTELLIGENCE_NODE_KINDS)[number];

export const ORG_INTELLIGENCE_EDGE_KINDS = [
  'supports',
  'depends_on',
  'constrains',
  'conflicts_with',
  'owned_by',
  'measured_by',
  'evidenced_by',
  'assumes',
  'blocks',
  'overrides',
  'approves',
  'activates',
  'monitors',
  'produces_outcome',
] as const;

export type OrgIntelligenceEdgeKindDto = (typeof ORG_INTELLIGENCE_EDGE_KINDS)[number];

export const ORG_INTELLIGENCE_PROOF_LEVELS = [
  'verified',
  'source_linked',
  'design_stage',
  'assumption',
  'heuristic',
  'conflict',
  'gap',
  'unknown',
  'refused',
] as const;

export type OrgIntelligenceProofLevelDto = (typeof ORG_INTELLIGENCE_PROOF_LEVELS)[number];

export type OrgIntelligenceSourceObjectReferenceDto = {
  domain: string;
  objectType: string;
  objectId: string;
  versionId?: string | null;
  asOf?: string | null;
};

export type OrgIntelligenceSourceArtifactRefDto = {
  artifactId: string;
  artifactKind: string;
  sourceKind: string;
  sourceId: string;
  asOf: string | null;
};

export type OrgIntelligenceTemporalDto = {
  asOf: string | null;
  validFrom: string | null;
  validTo: string | null;
};

export type OrgIntelligenceNodeDto = OrgIntelligenceTemporalDto & {
  id: string;
  tenantId: string;
  kind: OrgIntelligenceNodeKindDto;
  label: string;
  description?: string | null;
  sourceObjectRef: OrgIntelligenceSourceObjectReferenceDto;
  proofLevel: OrgIntelligenceProofLevelDto;
  sensitivity: VcbSensitivityLevelDto;
  sourceArtifactRefs: OrgIntelligenceSourceArtifactRefDto[];
  createdBySystem: string;
  version: string;
  inputHash: string;
  propertiesJson: OrgIntelligenceJsonObjectDto;
};

export type OrgIntelligenceEdgeDto = OrgIntelligenceTemporalDto & {
  id: string;
  tenantId: string;
  kind: OrgIntelligenceEdgeKindDto;
  fromNodeId: string;
  toNodeId: string;
  label?: string | null;
  sourceObjectRef: OrgIntelligenceSourceObjectReferenceDto;
  proofLevel: OrgIntelligenceProofLevelDto;
  sensitivity: VcbSensitivityLevelDto;
  sourceArtifactRefs: OrgIntelligenceSourceArtifactRefDto[];
  createdBySystem: string;
  version: string;
  inputHash: string;
  propertiesJson: OrgIntelligenceJsonObjectDto;
};

export type OrgIntelligenceIntegrityCheckDto = {
  checkId: string;
  status: 'passed' | 'failed' | 'unknown';
  message: string;
  blocking: boolean;
  relatedNodeIds: string[];
  relatedEdgeIds: string[];
};

export type OrgIntelligenceSnapshotDto = {
  snapshotId: string;
  tenantId: string;
  strategicObjectiveId: string;
  objectiveNodeId: string;
  asOf: string;
  inputHash: string;
  nodes: OrgIntelligenceNodeDto[];
  edges: OrgIntelligenceEdgeDto[];
  integrityChecks: OrgIntelligenceIntegrityCheckDto[];
};

export type CrossObjectiveConflictDto = {
  conflictId: string;
  tenantId: string;
  primaryStrategicObjectiveId: string;
  conflictingStrategicObjectiveId: string;
  sharedConstraintNodeId: string | null;
  affectedNodeIds: string[];
  affectedEdgeIds: string[];
  proofLevel: OrgIntelligenceProofLevelDto;
  sensitivity: VcbSensitivityLevelDto;
  sourceArtifactRefs: OrgIntelligenceSourceArtifactRefDto[];
  summary: string;
  recommendedAction: 'surface_tradeoff' | 'defer' | 'escalate' | 'amend_plan' | 'unknown';
};

export type EnterpriseTradeoffSignalDto = {
  signalId: string;
  tenantId: string;
  strategicObjectiveId: string;
  conflictIds: string[];
  posture: 'none' | 'watch' | 'material_tradeoff' | 'blocking_conflict' | 'unknown';
  headline: string;
  consequenceOfIgnoring: string;
  ownerUserId: string | null;
  reviewCadence: string | null;
  proofLevel: OrgIntelligenceProofLevelDto;
  sensitivity: VcbSensitivityLevelDto;
};
